// src/components/TaxBaseExplainer.js
//
// Shared tax-base metadata and row-building helpers for the income
// classification table. Originally also held the chapter-page's rendered
// JSX sections (IncomeOverviewCard, IncomeTaxTable, MarriageCallout,
// WealthPicture, AutonomoExplainer, EventLessons/hasEventLessons) — those
// were deleted in ticket 034-firstyearpolish along with the old chapter
// page (src/pages/TriageResults.js, pre-rename) that was their only
// consumer. What remains below is data/logic only, no render components:
// the data shape (BASE_LABELS, CONFIDENCE_LABELS, row shape below)
// deliberately mirrors the calculator's incomeClassificationRules.ts
// vocabulary (spanish_tax_base, classification_confidence). CaseBriefPage.js
// imports BASE_LABELS from here for its calculator income-group headings
// (ticket 028) instead of keeping its own copy. buildIrpfRows and the rest
// are consumed by buildSnapshotFindings.js for the renamed TriageResults.js
// (formerly TriageResultsCompact.js).
//
// Row shape expected by buildIrpfRows's output:
//   {
//     id:         string   — unique key
//     label:      string   — "What you told us" cell
//     base:       'general' | 'savings' | 'uncertain'
//     confidence: 'high' | 'medium' | 'low'
//     why:        string | null   — short explanation, optional
//   }

import { getSlotReportLabel } from '../lib/slotConfig'

export const BASE_LABELS = {
  general:   'General base',
  savings:   'Savings base',
  uncertain: 'Uncertain',
  excluded:  'Excluded',
}

// Some rows carry a conservative headline classification (general base) with
// a disclosed alternative reading, mirroring the calculator's own pattern in
// incomeClassificationRules.ts: foreign_government_pension is classified
// general/medium "conservatively," with the comment "scenario driver shows
// the exempt alternative." Triage has no scenario engine, so the alternative
// reading is surfaced here as row-level caveat text instead — same headline
// classification as the calculator, same disclosed nuance, no new bucket.
// Keyed by flag id, not by income type, since the caveat is specific to
// *why* this particular flag is conservative, not a general property of
// "pension" as a category.
//
// Exported (ticket 025-beckhamincome, D3/D4/D1) so buildSnapshotFindings.js's
// buildBaselineRows() can read these same strings directly for
// baseline_investments/baseline_foreign_rental — those rows are spread into
// buildIrpfRows's return value separately from flagRows and never pass
// through the flagRows.map() lookup below, so they need to consult
// ROW_CAVEATS explicitly at construction time rather than relying on that
// lookup to reach them.
export const ROW_CAVEATS = {
  government_pension_treaty:
    "Classified here as general base, matching the conservative default the calculator uses for foreign government pensions. Some preparers treat government/civil-service pensions as excluded from the Spanish base entirely under the treaty's government-service provision — worth confirming your specific situation.",
  us_pension_treaty:
    "Includes US Social Security, whose Spanish treatment is genuinely disputed among preparers — some treat it as taxable only in the US and exempt in Spain, but AEAT's published guidance and at least one binding ruling treat it as taxable in Spain with a credit for US tax paid. Classified conservatively as general base here; confirm with a professional before assuming either way.",
  foreign_pension_treaty:
    "Classified conservatively as general base pending confirmation of Spain's treaty (if any) with the paying country — treaty provisions vary by country, so no single rule applies the way it does for a US pension.",
  // D1 (ticket 025): moved off the IncomeTaxTable call site's hardcoded
  // `note` prop, where it showed unconditionally to every Beckham filer
  // regardless of whether they actually own unoccupied Spanish property.
  // Reached automatically via buildIrpfRows's flagRows.map() below whenever
  // the imputed_rent flag actually fires — the flag's own summary (see
  // runTriageFlags.ts) already states the Beckham-specific filing mechanics;
  // this only adds the court-dispute wrinkle on top.
  imputed_rent:
    "One live wrinkle: whether Beckham filers must report imputed income on a Spanish primary residence is currently contested between two Spanish courts - worth asking your gestor which approach they're taking this filing year.",
  // D3 (ticket 025): triage collects no source-country data for investment
  // income at all (investment_account_location is only asked in
  // ProfilePage.js, never in the triage flow) — foreign-source investment
  // income is genuinely exempt under Beckham, but Spanish-source isn't, and
  // we don't know which this user has.
  baseline_investments:
    "Under Beckham, foreign-source investment income is generally exempt, but Spanish-source investment income isn't — confirm which applies to your accounts with an advisor.",
  // D3 (ticket 025): unlike investments, this row is already gated on
  // confirmed foreign sourcing (hasRentalIncome && !hasSpanishRental), and
  // research states foreign real-estate income is neither declared nor taxed
  // in Spain under Beckham while the regime applies — a more confident,
  // exemption-leaning claim than the investments case above.
  baseline_foreign_rental:
    "Under Beckham, rental income from property outside Spain is generally not declared or taxed in Spain while the regime applies — confirm this matches your specific situation with an advisor.",
}

// D4 (ticket 025): unlike the plain strings above, this caveat varies by
// selfEmploymentCountry (already a confirmed intake fact from
// self_employment_country, not a guess) — kept as a function rather than a
// second ROW_CAVEATS string so buildSnapshotFindings.js shares one
// definition instead of duplicating the location-clause logic.
// Self-employment/Beckham compatibility hinges on an ENISA "entrepreneurial
// activity" qualification (2023 Startup Law reform), not sourcing — so the
// country only flavors the sentence, it doesn't change the row's
// base/confidence, which stay uncertain/low either way.
export function beckhamSelfEmploymentCaveat(selfEmploymentCountry) {
  const locationClause = selfEmploymentCountry === 'spain'
    ? ', even though the activity itself is Spain-based'
    : selfEmploymentCountry
    ? ', particularly since the activity is based outside Spain'
    : ''
  return `Self-employment can be compatible with Beckham only for a qualifying "entrepreneurial activity" classification (a formal ENISA report requirement) — whether your specific activity qualifies needs a professional review, not an assumption either way${locationClause}.`
}

// Rate context strings, read directly by buildSnapshotFindings.js's
// buildIncomeTableFinding (table.rateText) rather than being typed a second
// time — a future correctness fix to the savings range (see the parallel
// irpfCore.ts SAVINGS_BRACKETS ticket) propagates automatically.
export const BASE_RATE_TEXT = {
  general:   'Progressive rates up to 47%',
  // Source of truth: SAVINGS_RATE_RANGE_DISPLAY in
  // supabase/functions/_shared/calculators/irpfCore.ts. This is a frontend
  // .js file and can't import that Deno module, so this string is hand-synced
  // — keep it matching if the engine constant ever changes again. Reflects
  // the 2025/2026 schedule (2024's top rate is 28%, unchanged).
  savings:   '19% – 30%',
  uncertain: 'Needs classification review',
}

// D2 (ticket 025-beckhamincome): Beckham doesn't tax the general base at
// ordinary progressive rates — employment/pension income is taxed worldwide
// at Beckham's own flat 24%/47% schedule (Modelo 151), per D5/D6. Only
// 'general' actually needs different text: 'savings' should realistically
// never appear for a Beckham filer's table once baseline_investments'
// beckhamActive branch ships (D3), and 'uncertain' is already generic enough
// to be correct regardless of Beckham status, so both fall back to
// BASE_RATE_TEXT above rather than being redefined here.
export const BECKHAM_RATE_TEXT = {
  general: 'Flat 24% up to €600,000 (Modelo 151) — worldwide for employment income',
}

// ─── Helpers to build rows from triage flags ──────────────────────────────

// componentFlags: irpfComponentFlags array (from runTriageFlags.ts via
// deriveFacts.ts), already carrying spanishTaxBase/taxBaseConfidence.
// baselineRows: display-only rows sourced directly from confirmed derived
// facts for income types that don't produce their own flag — salary,
// self-employment, Spanish pension, ongoing dividends/interest, foreign
// rental for a resident. Built in buildSnapshotFindings.js's
// buildBaselineRows().
// Flag titles are written for task-card context ("Retirement account
// withdrawal needs Spain-side review") where the review clause is part of
// the sentence. In the table, review status is already shown as its own
// tag next to the label, so repeating it inside the label text reads as a
// run-on. This gives the table a clean, short fact name instead — falls
// back to the full flag.title for anything not listed, so a new flag id
// never silently disappears, it just renders with the longer title until
// someone adds a short form here.
const ROW_LABEL_OVERRIDES = {
  spanish_rental:                       'Spanish rental income',
  imputed_rent:                         'Imputed rental income',
  capital_gains:                        'Capital gains',
  foreign_pension_treaty:               'Foreign pension income',
  equity_compensation:                  'Equity compensation (RSUs / options)',
  roth_withdrawal_uncertain:            'Roth withdrawal',
  retirement_withdrawal_review:         'Retirement account withdrawal',
}

// us_pension_treaty / government_pension_treaty short labels — content-review
// pass fix (023-snapshotredesign): these two flags are mutually exclusive by
// construction (runTriageFlags.ts branches on facts.pensionIsGovernment to
// decide which one fires), so which flag fired already tells us the
// government/private distinction. Previously two independently hand-written
// ROW_LABEL_OVERRIDES strings ('US pension / Social Security' / 'US
// government pension') duplicated that distinction instead of reading it
// from the same pension_is_government report-label tier ticket 019 added
// (slotConfig.js) — same D3 pattern used just below for
// business_partnership_classification.
function usPensionRowLabel(flagId) {
  return `US ${getSlotReportLabel('pension_is_government', flagId === 'government_pension_treaty')}`
}

// businessPartnershipType: facts.businessPartnershipType, used to give the
// business_partnership_classification row its short label via slotConfig.js's
// getSlotReportLabel instead of a ROW_LABEL_OVERRIDES entry — see ticket
// 023-snapshotredesign's D3. Reuses the same report-label tier
// CaseBriefPage.js already depends on for the same slot, rather than a
// second, parallel three-value label map.
export function buildIrpfRows(componentFlags, baselineRows = [], businessPartnershipType = null) {
  const flagRows = (componentFlags || [])
    .filter(f => f.spanishTaxBase) // only flags we've actually mapped
    .map(f => ({
      id:         f.id,
      label:      (f.id === 'business_partnership_classification' && businessPartnershipType)
        ? getSlotReportLabel('business_partnership_type', businessPartnershipType)
        : (f.id === 'us_pension_treaty' || f.id === 'government_pension_treaty')
        ? usPensionRowLabel(f.id)
        : ROW_LABEL_OVERRIDES[f.id] || f.title,
      base:       f.spanishTaxBase,
      confidence: f.taxBaseConfidence,
      caveat:     ROW_CAVEATS[f.id] || null,
    }))
  return [...baselineRows, ...flagRows]
}
